import React, { useState, useRef } from 'react';
import { FiPlayCircle, FiDownload, FiFileText, FiMonitor, FiVideo, FiEye, FiX, FiMaximize } from 'react-icons/fi';

const SalesTutorials = () => {
  const [tutorials] = useState([
    {
      id: 1,
      title: 'Sales Workshop Redesigned',
      description: 'Comprehensive presentation on the redesigned sales workflow, including best practices for lead conversions.',
      type: 'presentation',
      url: '/assets/tutorials/Sales_Workshop_Redesigned.pdf',
      thumbnail: 'bg-blue-100',
      icon: <FiMonitor className="w-12 h-12 text-blue-500" />,
      duration: '45 Slides',
      date: 'July 21, 2026'
    }
  ]);

  const [selectedPresentation, setSelectedPresentation] = useState(null);

  const openViewer = (tutorial) => {
    // Office viewer requires an absolute, publicly accessible URL.
    // If on localhost, it might not work, but we construct the full URL anyway.
    const fullUrl = window.location.origin + tutorial.url;
    setSelectedPresentation({ ...tutorial, fullUrl });
  };

  const iframeRef = useRef(null);

  const toggleFullScreen = () => {
    if (iframeRef.current) {
      if (iframeRef.current.requestFullscreen) {
        iframeRef.current.requestFullscreen();
      } else if (iframeRef.current.webkitRequestFullscreen) { /* Safari */
        iframeRef.current.webkitRequestFullscreen();
      } else if (iframeRef.current.msRequestFullscreen) { /* IE11 */
        iframeRef.current.msRequestFullscreen();
      }
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-1.5 sm:mb-2">Sales Tutorials</h1>
        <p className="text-xs sm:text-sm text-slate-500">Learn best practices and discover how to effectively use the Sales module.</p>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tutorials.map((tutorial) => (
          <div 
            key={tutorial.id} 
            className="group bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md hover:border-slate-300 transition-all duration-300 flex flex-col"
          >
            {/* Thumbnail */}
            <div className={`h-40 ${tutorial.thumbnail} flex items-center justify-center relative`}>
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-300"></div>
              {tutorial.icon}
            </div>

            {/* Content */}
            <div className="p-5 flex flex-col flex-grow">
              <div className="flex items-center gap-2 text-xs font-medium text-slate-400 mb-2">
                <span className="uppercase tracking-wider">{tutorial.type}</span>
                <span>•</span>
                <span>{tutorial.duration}</span>
              </div>
              
              <h3 className="text-lg font-bold text-slate-800 mb-2 leading-tight group-hover:text-blue-600 transition-colors">
                {tutorial.title}
              </h3>
              
              <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                {tutorial.description}
              </p>
              
              <div className="mt-auto flex items-center justify-between pt-4 border-t border-slate-100">
                <span className="text-xs text-slate-400 font-medium">{tutorial.date}</span>
                <div className="flex gap-2">
                  {tutorial.type === 'presentation' && (
                    <button 
                      onClick={() => openViewer(tutorial)}
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 text-sm font-medium rounded-lg border border-blue-200 transition-colors"
                    >
                      <FiEye className="w-4 h-4" /> View
                    </button>
                  )}
                  <a 
                    href={tutorial.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-slate-50 hover:bg-slate-100 text-slate-700 text-sm font-medium rounded-lg border border-slate-200 transition-colors"
                  >
                    {tutorial.type === 'presentation' ? (
                      <>
                        <FiDownload className="w-4 h-4" /> Download
                      </>
                    ) : (
                      <>
                        <FiPlayCircle className="w-4 h-4" /> Watch Now
                      </>
                    )}
                  </a>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Viewer Modal */}
      {selectedPresentation && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm">
          <div className="bg-white w-full max-w-6xl h-[85vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-100 gap-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 text-blue-600 rounded-lg shrink-0">
                  <FiMonitor className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-bold text-slate-800 text-sm sm:text-base truncate">{selectedPresentation.title}</h3>
                  <p className="text-xs text-slate-500">Presentation Viewer</p>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-4 self-end sm:self-auto">
                <a 
                  href={selectedPresentation.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs sm:text-sm font-medium text-slate-600 hover:text-blue-600 flex items-center gap-1.5 transition-colors"
                >
                  <FiDownload className="w-4 h-4" />
                  <span className="hidden sm:inline">Download PDF</span>
                </a>
                <button 
                  onClick={toggleFullScreen}
                  className="text-xs sm:text-sm font-medium text-slate-600 hover:text-blue-600 flex items-center gap-1.5 transition-colors"
                  title="Full Screen"
                >
                  <FiMaximize className="w-4 h-4" />
                  <span className="hidden sm:inline">Full Screen</span>
                </button>
                <button 
                  onClick={() => setSelectedPresentation(null)}
                  className="p-1.5 sm:p-2 hover:bg-slate-100 text-slate-500 hover:text-slate-700 rounded-full transition-colors cursor-pointer"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="flex-grow bg-slate-50 relative flex flex-col overflow-hidden">
              <iframe 
                ref={iframeRef}
                src={selectedPresentation.url}
                className="w-full h-full border-none"
                title={selectedPresentation.title}
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesTutorials;
